import { FC } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

interface Props {
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onPrimaryAction: () => void;
  onSecondaryAction?: () => void;
  primaryIcon?: React.ReactNode;
}

const ErrorScreen: FC<Props> = ({
  title,
  description,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction,
  primaryIcon = <Refresh />,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={{ xs: '100vh', sm: '50vh' }}
      px={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <ErrorOutline
          color="error"
          sx={{
            fontSize: { xs: 48, sm: 60 },
            mb: 2,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 },
            },
          }}
        />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          paragraph
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            maxWidth: '90%',
            mx: 'auto',
          }}
        >
          {description}
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
          }}
        >
          {isMobile ? (
            <>
              <Button
                variant="contained"
                startIcon={primaryIcon}
                onClick={onPrimaryAction}
                fullWidth
                sx={{
                  py: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {primaryButtonText}
              </Button>
              {secondaryButtonText && onSecondaryAction && (
                <Button
                  variant="outlined"
                  onClick={onSecondaryAction}
                  fullWidth
                  sx={{ py: 1.5 }}
                >
                  {secondaryButtonText}
                </Button>
              )}
            </>
          ) : (
            <>
              {secondaryButtonText && onSecondaryAction && (
                <Button
                  variant="outlined"
                  onClick={onSecondaryAction}
                  sx={{ minWidth: 120 }}
                >
                  {secondaryButtonText}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={primaryIcon}
                onClick={onPrimaryAction}
                sx={{
                  minWidth: 120,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {primaryButtonText}
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ErrorScreen;
